import * as Sentry from "@sentry/react-native";
import { t } from "@transifex/native";
import { useFocusEffect, usePathname, useRouter } from "expo-router";
import { useCallback, useState } from "react";

import { useContactValidation } from "@/hooks/useContactValidation";
import { useSelectPlate } from "@/hooks/useSelectPlate";
import { fetchPlate } from "@/services/plate.queries";
import { validateLicensePlate } from "@/utils/licensePlateValidation";
import { getValueFromStorage, pushToStorageArray, saveToStorage } from "@/utils/secureStore";

import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import ContactInput from "@/components/shared/LicensePlatePicker/ContactInput";
import { LicensePlateInput } from "@/components/shared/LicensePlatePicker/LicensePlateInput";
import { LicensePlateRadio } from "@/components/shared/LicensePlatePicker/LicensePlateRadio";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PlateModal from "@/components/shared/PlateModal";
import PlateWithWarningModal from "@/components/shared/PlateWithWarningModal";
import SessionInProgressModal from "@/components/shared/SessionInProgressModal";
import TermsTextLink from "@/components/shared/TermsTextLink";
import { Box } from "@/components/ui/box";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useCustomTheme } from "@/hooks/useCustomTheme";
import { useToastNotify } from "@/stores/toastStore";
import { isEmail, isPhone } from "@/utils/etc";
import { getPhoneDigits } from "@/utils/formatPhoneNumber";
import type { AlreadyPaidData, WarningData } from "ipctg-inflow-types/InFlowAPIMisc";
import { ContactType } from "ipctg-inflow-types/InFlowEnums";
import { Platform } from "react-native";

export default function PurchaseSinglePass() {
  const router = useRouter();
  const pathname = usePathname();
  const {
    guestPlates,
    fetchGuestPlates,
    inputValue: licensePlateInputValue,
    setInputValue: setLicensePlateInputValue,
    setLicensePlate,
    selectedPlate,
    setSelectedPlate,
    isRemoveModalOpen,
    setIsRemoveModalOpen,
    plateToRemove,
    isRemoving,
    errors,
    setErrors,
    handleDeletePlate,
    confirmRemovePlate,
    handleLicensePlateChange,
    clearLicensePlateError,
    account,
    isAccountFetching,
    refetchAccount,
  } = useSelectPlate();
  const {
    theme: { defaultContactType },
  } = useCustomTheme();

  /*
    Needs to support undefined value so we know if this is fresh or not due
    to how React Native lifecycles work :/
  */
  const [contactValue, setContactValue] = useState<Record<ContactType, string>>();
  const [contactMode, setContactMode] = useState<ContactType>(defaultContactType);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [plateInputError, setPlateInputError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionInProgressModalOpen, setIsSessionInProgressModalOpen] = useState(false);
  const [alreadyPaidData, setAlreadyPaidData] = useState<AlreadyPaidData | null>(null);
  const [isPlateWarningDataModalOpen, setIsPlateWarningDataModalOpen] = useState(false);
  const [plateWarningData, setPlateWarningData] = useState<WarningData | null>(null);

  const { errorMessage, validateContact, clearError } = useContactValidation();
  const { notify } = useToastNotify();

  const resetState = useCallback(() => {
    setLicensePlateInputValue("");
    setLicensePlate("");
    setSelectedPlate("");
    setPlateInputError(false);

    setContactMode(defaultContactType);
    setContactValue(undefined);

    setErrors({ radio: null, licensePlate: null, contact: null });
  }, [setLicensePlateInputValue, setLicensePlate, setSelectedPlate, setErrors, defaultContactType]);

  // I guess we are resetting stuff here whenever we reload the page
  useFocusEffect(
    useCallback(() => {
      if (pathname === "/purchase/singlepass") {
        resetState();
        refetchAccount();
      }
    }, [pathname, resetState, refetchAccount]),
  );

  useFocusEffect(
    useCallback(() => {
      const loadContact = async () => {
        const defaultMode = defaultContactType || ContactType.Phone;
        let contact: Record<ContactType, string> = {
          Phone: "",
          Email: "",
        };

        if (account) {
          contact = {
            Phone: account.phone || "",
            Email: account.email || "",
          };
        } else {
          const storedContact = await getValueFromStorage("contact");

          if (storedContact) {
            contact = {
              Phone: isPhone(storedContact) ? storedContact : "",
              Email: isEmail(storedContact) ? storedContact : "",
            };
          }
          fetchGuestPlates();
        }

        const otherMode = defaultMode === ContactType.Phone ? ContactType.Email : ContactType.Phone;
        const mode = contact[defaultContactType] ? defaultMode : contact[otherMode] ? otherMode : defaultMode;

        setContactValue(contact);
        setContactMode(mode);
      };

      if (contactValue === undefined && !isAccountFetching) {
        loadContact();
      }
    }, [account, contactValue, isAccountFetching, fetchGuestPlates, defaultContactType]),
  );

  const handleSubmit = async () => {
    const newErrors: {
      radio: string | null;
      licensePlate: string | null;
      contact: string | null;
    } = {
      radio: null,
      licensePlate: null,
      contact: null,
    };

    if (account?.savedPlates && account.savedPlates.length > 0 && !selectedPlate) {
      newErrors.radio = t("Please select an option.");
    } else if (!account && guestPlates?.length && !selectedPlate) {
      newErrors.radio = t("Please select an option.");
    }

    const plateError = validateLicensePlate(licensePlateInputValue);
    if (plateError) {
      newErrors.licensePlate = t(plateError);
      setPlateInputError(true);
    } else {
      setPlateInputError(false);
    }
    const contact = contactValue?.[contactMode];
    const contactValid = validateContact({ contact, isPhoneForm: contactMode === ContactType.Phone });

    if (!contactValid) {
      newErrors.contact = errorMessage;
    }

    setErrors(newErrors);

    if (Object.values(newErrors).every((error) => error === null)) {
      setIsLoading(true);

      try {
        if (contact) {
          await saveToStorage("contact", !isEmail(contact) ? `+${getPhoneDigits(contact)}` : contact);
        }
        const response = await fetchPlate({ plate: licensePlateInputValue });
        if (response.alreadyPaid) {
          setAlreadyPaidData(response.alreadyPaidData);
          setIsSessionInProgressModalOpen(true);
        } else if (response.warning && response.warningData) {
          setIsPlateWarningDataModalOpen(true);
          setPlateWarningData(response.warningData);
        } else if (!response.plateInConvenience) {
          setIsModalOpen(true);
        } else {
          await saveToStorage("licensePlate", licensePlateInputValue);
          router.push("/selectrate");
        }
      } catch (error) {
        console.error("Error fetching plate information:", error);
        Sentry.captureException(error, {
          extra: {
            data: { plate: licensePlateInputValue },
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
        notify({
          severity: "error",
          message: t("An error occurred while processing your request."),
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSelectPlate = (value: string) => {
    const formattedPlate = value.trim().toLocaleUpperCase();
    setSelectedPlate(formattedPlate);
    if (formattedPlate !== "NEW") {
      setLicensePlateInputValue(formattedPlate);
      setLicensePlate(formattedPlate);
      setPlateInputError(false);
    } else {
      setLicensePlateInputValue("");
      setLicensePlate("");
      setPlateInputError(true);
    }
    setErrors((prevErrors) => ({ ...prevErrors, radio: null }));
  };

  if (isAccountFetching) {
    return <PurchaseSinglePassSkeleton hasSavedPlates={!!account?.savedPlates?.length} />;
  }

  const handleConfirm = async () => {
    try {
      if (!account) {
        await pushToStorageArray("guestRecentPlates", licensePlateInputValue);
      }
      await saveToStorage("licensePlate", licensePlateInputValue);
      const contact = contactValue?.[contactMode];
      if (contact) {
        await saveToStorage("contact", !isEmail(contact) ? `+${getPhoneDigits(contact)}` : contact);
      }
      setIsModalOpen(false);
      setIsPlateWarningDataModalOpen(false);
      router.push("/selectrate");
    } catch (error) {
      console.error("Error saving data:", error);
      Sentry.captureException(error, {
        extra: {
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
      notify({
        severity: "error",
        message: t("An error occurred while processing your request."),
      });
    }
  };

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Please provide your information")} />

        <VStack className="gap-[20px]">
          {account?.savedPlates && account.savedPlates.length > 0 ? (
            <>
              <LicensePlateRadio
                selectedValue={selectedPlate}
                onValueChange={handleSelectPlate}
                plates={account.savedPlates.map((plate) => ({
                  id: plate.plate,
                  plate: plate.plate,
                }))}
                onDelete={handleDeletePlate}
              />

              {errors.radio && (
                <Box>
                  <Text className="text-error-100 text-[14px] leading-[20px]">{errors.radio}</Text>
                </Box>
              )}
            </>
          ) : (
            // SHOW INPUT IF THERE ISNT SAVED PLATES
            <>
              {!account && guestPlates && guestPlates.length > 0 ? (
                <>
                  <LicensePlateRadio
                    selectedValue={selectedPlate}
                    onValueChange={handleSelectPlate}
                    plates={guestPlates.map((plate) => ({
                      id: plate,
                      plate: plate,
                    }))}
                    onDelete={handleDeletePlate}
                  />
                  {errors.radio && (
                    <Box>
                      <Text className="text-error-100 text-[14px] leading-[20px]">{errors.radio}</Text>
                    </Box>
                  )}
                </>
              ) : (
                <LicensePlateInput
                  onAdd={handleLicensePlateChange}
                  setInputValue={setLicensePlateInputValue}
                  inputValue={licensePlateInputValue}
                  setInputError={setPlateInputError}
                  inputError={plateInputError}
                  errorMessage={errors.licensePlate}
                  clearError={clearLicensePlateError}
                />
              )}
            </>
          )}
          {/* SHOW INPUT IF "ENTER NEW PLATE" IS SELECTED */}
          {selectedPlate === "NEW" && (
            <LicensePlateInput
              onAdd={handleLicensePlateChange}
              setInputValue={setLicensePlateInputValue}
              inputValue={licensePlateInputValue}
              setInputError={setPlateInputError}
              inputError={plateInputError}
              errorMessage={errors.licensePlate}
              clearError={clearLicensePlateError}
            />
          )}
        </VStack>
        <Box className="gap-[20px]">
          <ContactInput
            mode={contactMode}
            value={contactValue || { Phone: "", Email: "" }}
            onChange={(value) => {
              setContactValue((current) => ({ Phone: current?.Phone || "", Email: current?.Email || "", [contactMode]: value }));
            }}
            errorMessage={errorMessage}
            clearError={clearError}
            onUpdateMode={(mode) => setContactMode(mode)}
          />
          <TermsTextLink />
        </Box>
        <HStack className="gap-[12px]">
          <AppButton
            onPress={() => {
              resetState();
              if (!router.canGoBack()) {
                router.replace("/welcome");
              } else {
                router.back();
              }
            }}
            title={t("Back")}
            variant="outline"
            iconPosition="before"
            icon={ArrowLeft}
          />
          <AppButton
            className="flex-1"
            loading={isLoading}
            onPress={handleSubmit}
            title={t("Agree and Continue")}
            variant="primary"
            icon={ArrowRight}
            iconPosition="after"
          />
        </HStack>
      </VStack>

      <PlateModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} licensePlate={licensePlateInputValue} onConfirm={handleConfirm} type={"confirm"} />

      <PlateModal
        isOpen={isRemoveModalOpen}
        type="remove"
        licensePlate={plateToRemove}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={confirmRemovePlate}
        isRemoving={isRemoving}
      />

      <SessionInProgressModal
        isOpen={isSessionInProgressModalOpen}
        onClose={() => setIsSessionInProgressModalOpen(false)}
        alreadyPaidData={alreadyPaidData}
        plate={licensePlateInputValue}
      />

      <PlateWithWarningModal
        isOpen={isPlateWarningDataModalOpen}
        onClose={() => setIsPlateWarningDataModalOpen(false)}
        cameraLotName={plateWarningData?.cameraLotName ?? ""}
        qrLotName={plateWarningData?.qrLotName ?? ""}
        onConfirm={handleConfirm}
      />
    </Screen>
  );
}

const PurchaseSinglePassSkeleton = ({ hasSavedPlates = false }) => {
  return (
    <Screen>
      <VStack className="gap-[32px]">
        <Box className="flex justify-center items-center">
          <SkeletonText className="h-[20px] w-3/4" />
        </Box>

        <VStack className="gap-[20px]">
          {hasSavedPlates ? (
            <VStack className="gap-[12px]">
              <SkeletonText className="h-[14px] w-1/2" />
              {[1, 2].map((i) => (
                <HStack key={i} className="items-center gap-[12px]">
                  <Skeleton className="h-[52px] w-full rounded-md" />
                </HStack>
              ))}
            </VStack>
          ) : (
            <Box>
              <Skeleton className="h-[52px] w-full rounded-md" />
            </Box>
          )}
        </VStack>

        <VStack className="gap-[12px]">
          <SkeletonText className="h-[14px] w-1/2" />
          <Skeleton className="h-[52px] w-full rounded-md" />
        </VStack>
        <VStack className="gap-[8px] items-center">
          <SkeletonText className="h-[14px] w-3/4" />
          <SkeletonText className="h-[14px] w-1/2" />
        </VStack>

        <HStack className="gap-[12px]">
          <Skeleton className="h-[52px] w-1/3 rounded-md" />
          <Skeleton className="h-[52px] w-2/3 rounded-md" />
        </HStack>
      </VStack>
    </Screen>
  );
};
